<script setup lang="ts">
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/dropdown-menu'
import { useRouter } from 'vue-router'
import type { MenuItemType } from '@/components/dropdown-menu/types'
import NestedMenuItem from '@/components/dropdown-menu/NestedMenuItem.vue'

const props = defineProps({
  items: {
    type: Array as () => MenuItemType[],
    required: true
  },
  triggerIcon: {
    type: String,
    default: 'more_horiz'
  },
  triggerAriaLabel: {
    type: String,
    default: 'Menu'
  },
  align: {
    type: String as () => 'start' | 'center' | 'end',
    default: 'end'
  },
  containerPortal: {
    type: String,
    default: undefined
  },
  triggerClass: {
    type: String,
    default:
      'flex items-center justify-center w-8 h-8 rounded-full text-gray-900 hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-700 focus-visible:bg-gray-300'
  },
  triggerIconClass: {
    type: String,
    default: ''
  },
  triggerFsExclude: {
    type: Boolean,
    default: false
  },
  // Whether the open menu traps focus and aria-hides the rest of the page.
  // Non-modal callers (e.g. a lightweight row-actions menu) should pass
  // `false` — the reka-ui modal path calls the `aria-hidden` package's
  // hideOthers() on document.body without excluding focusable descendants
  // from tab order, which is a WCAG 4.1.2 aria-hidden-focus violation.
  modal: {
    type: Boolean,
    default: true
  }
})

const router = useRouter()

const handleAction = (item: MenuItemType) => {
  if (item.disabled) return
  if (item.action) {
    item.action()
  } else if (item.route) {
    router.push(item.route)
  }
}
</script>

<template>
  <DropdownMenu :modal="modal">
    <DropdownMenuTrigger
      :class="[triggerClass, { 'fs-exclude': triggerFsExclude }]"
      :aria-label="triggerAriaLabel"
      aria-haspopup="menu"
    >
      <span class="material-icons-round" :class="triggerIconClass">{{ triggerIcon }}</span>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-40 px-0 py-2" :align="align" :containerPortal="props.containerPortal">
      <template v-for="(item, index) in items" :key="index">
        <NestedMenuItem :item="item" />
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
